/**
 * Transport - Standalone timing/scheduling system
 *
 * Provides sample-accurate scheduling using Web Audio's clock.
 * Works in both browser and Node.js (via node-web-audio-api).
 *
 * Key concepts:
 * - Uses audioContext.currentTime as the master clock
 * - Lookahead scheduling: callbacks fire slightly early with precise time
 * - Musical time: BPM, ticks (PPQ=48), bars:beats:sixteenths notation
 *
 * No Tone.js dependency - pure Web Audio + JS scheduling.
 */

export type TransportState = 'started' | 'stopped' | 'paused';
export type TransportEventName = 'start' | 'stop' | 'pause' | 'loop';
export type TransportCallback = (time: number) => void;

// Musical time types
export type Subdivision = '1m' | '2m' | '4m' | '1n' | '2n' | '4n' | '8n' | '16n' | '32n';
export type Time = number | string | Subdivision;

// PPQ = 48 (48 ticks = 1 quarter note) - matches ClockNode
const PPQ = 48;

// Subdivision to ticks lookup
const SUBDIVISION_TICKS: Record<Subdivision, number> = {
  '1m': 192,  // 4 beats * 48
  '2m': 384,
  '4m': 768,
  '1n': 192,  // whole note
  '2n': 96,   // half note
  '4n': 48,   // quarter note
  '8n': 24,   // eighth note
  '16n': 12,  // sixteenth note
  '32n': 6,   // thirty-second note
};

interface ScheduledEvent {
  id: number;
  callback: TransportCallback;
  time: number;  // in ticks
  repeat?: number;  // repeat interval in ticks (undefined = one-shot)
  duration?: number;  // for repeat events, when to stop (in ticks, Infinity = forever)
  disposed: boolean;
}

interface TransportOptions {
  audioContext: AudioContext | BaseAudioContext;
  bpm?: number;
  ppq?: number;
  beatsPerBar?: number;  // time signature numerator (default: 4)
  lookahead?: number;  // seconds to look ahead
  updateInterval?: number;  // ms between scheduler checks
}

export class Transport {
  private audioContext: AudioContext | BaseAudioContext;
  private _bpm: number;
  private _ppq: number;
  private _beatsPerBar: number;
  private _state: TransportState = 'stopped';

  // Timing
  private _startTime: number = 0;  // audioContext time when transport started
  private _pauseTime: number = 0;  // audioContext time when paused
  private _tickOffset: number = 0;  // tick position when started/resumed

  // Lookahead scheduling
  private lookahead: number;  // seconds to schedule ahead
  private updateInterval: number;  // ms between scheduler updates
  private schedulerTimer: ReturnType<typeof setInterval> | null = null;
  private lastScheduledTick: number = -1;

  // Events
  private events: Map<number, ScheduledEvent> = new Map();
  private nextEventId: number = 1;

  // Event emitter
  private listeners: Map<TransportEventName, Set<TransportCallback>> = new Map();

  constructor(options: TransportOptions) {
    this.audioContext = options.audioContext;
    this._bpm = options.bpm ?? 120;
    this._ppq = options.ppq ?? PPQ;
    this._beatsPerBar = options.beatsPerBar ?? 4;
    this.lookahead = options.lookahead ?? 0.1;  // 100ms default
    this.updateInterval = options.updateInterval ?? 25;  // 25ms default
  }

  // ─────────────────────────────────────────────────────────────
  // Time Conversion
  // ─────────────────────────────────────────────────────────────

  /** Convert various time formats to ticks */
  toTicks(time: Time): number {
    if (typeof time === 'number') {
      return time;
    }

    // Check for tick notation (e.g., "48i")
    if (time.endsWith('i')) {
      return parseInt(time.slice(0, -1), 10);
    }

    // Check for bars:beats:sixteenths notation
    if (time.includes(':')) {
      const [bars, beats, sixteenths] = time.split(':').map(Number);
      return (bars * this._beatsPerBar + beats) * this._ppq + (sixteenths * this._ppq / 4);
    }

    // Check subdivision notation
    if (time in SUBDIVISION_TICKS) {
      return SUBDIVISION_TICKS[time as Subdivision];
    }

    // Try to parse as seconds (e.g., "1.5s")
    if (time.endsWith('s')) {
      const seconds = parseFloat(time.slice(0, -1));
      return this.secondsToTicks(seconds);
    }

    throw new Error(`Unknown time format: ${time}`);
  }

  /** Convert ticks to seconds at current BPM */
  ticksToSeconds(ticks: number): number {
    const beatsPerSecond = this._bpm / 60;
    const ticksPerSecond = beatsPerSecond * this._ppq;
    return ticks / ticksPerSecond;
  }

  /** Convert seconds to ticks at current BPM */
  secondsToTicks(seconds: number): number {
    const beatsPerSecond = this._bpm / 60;
    const ticksPerSecond = beatsPerSecond * this._ppq;
    return seconds * ticksPerSecond;
  }

  /** Convert ticks to bars:beats:sixteenths string */
  ticksToPosition(ticks: number): string {
    const ticksPerBeat = this._ppq;
    const ticksPerBar = ticksPerBeat * this._beatsPerBar;
    const ticksPerSixteenth = ticksPerBeat / 4;

    const bars = Math.floor(ticks / ticksPerBar);
    const remainingAfterBars = ticks % ticksPerBar;
    const beats = Math.floor(remainingAfterBars / ticksPerBeat);
    const remainingAfterBeats = remainingAfterBars % ticksPerBeat;
    const sixteenths = Math.floor(remainingAfterBeats / ticksPerSixteenth);

    return `${bars}:${beats}:${sixteenths}`;
  }

  // ─────────────────────────────────────────────────────────────
  // Current State
  // ─────────────────────────────────────────────────────────────

  /** Current audioContext time */
  get currentTime(): number {
    return this.audioContext.currentTime;
  }

  /** Transport state: 'started', 'stopped', or 'paused' */
  get state(): TransportState {
    return this._state;
  }

  /** BPM (beats per minute) */
  get bpm(): { value: number } {
    // Return object with .value for Tone.js-like API
    const self = this;
    return {
      get value() { return self._bpm; },
      set value(v: number) { self._bpm = v; }
    };
  }

  /** Pulses per quarter note */
  get PPQ(): number {
    return this._ppq;
  }

  /** Beats per bar (time signature numerator, default 4) */
  get beatsPerBar(): number {
    return this._beatsPerBar;
  }
  set beatsPerBar(v: number) {
    this._beatsPerBar = v;
  }

  /** Current bar number (1-indexed) */
  get bar(): number {
    const ticksPerBar = this._ppq * this._beatsPerBar;
    return Math.floor(this.ticks / ticksPerBar) + 1;
  }

  /** Current beat within the bar (1-indexed, e.g. 1–4 in 4/4) */
  get beat(): number {
    const ticksPerBar = this._ppq * this._beatsPerBar;
    return Math.floor((this.ticks % ticksPerBar) / this._ppq) + 1;
  }

  /** Current position in ticks */
  get ticks(): number {
    if (this._state === 'stopped') {
      return 0;
    }
    if (this._state === 'paused') {
      return this._tickOffset;
    }
    // Calculate current ticks from elapsed time
    const elapsed = this.audioContext.currentTime - this._startTime;
    return this._tickOffset + this.secondsToTicks(elapsed);
  }
  set ticks(t: number) {
    if (this._state === 'started') {
      // Adjust startTime to make current position = t
      this._tickOffset = t;
      this._startTime = this.audioContext.currentTime;
    } else {
      this._tickOffset = t;
    }
  }

  /** Current position as bars:beats:sixteenths */
  get position(): string {
    return this.ticksToPosition(this.ticks);
  }
  set position(p: string) {
    this.ticks = this.toTicks(p);
  }

  /** Current position in seconds */
  get seconds(): number {
    return this.ticksToSeconds(this.ticks);
  }
  set seconds(s: number) {
    this.ticks = this.secondsToTicks(s);
  }

  // ─────────────────────────────────────────────────────────────
  // Playback Control
  // ─────────────────────────────────────────────────────────────

  /** Start the transport */
  start(time?: number, offset?: Time): this {
    if (this._state === 'started') return this;

    const startTime = time ?? this.audioContext.currentTime;

    if (this._state === 'paused') {
      // Resume from paused position
      this._startTime = startTime;
      // _tickOffset already set from pause
    } else {
      // Fresh start
      this._startTime = startTime;
      this._tickOffset = offset !== undefined ? this.toTicks(offset) : 0;
      this.lastScheduledTick = this._tickOffset - 1;
    }

    this._state = 'started';
    this.startScheduler();
    this.emit('start', startTime);

    return this;
  }

  /** Stop the transport (resets position to 0) */
  stop(time?: number): this {
    if (this._state === 'stopped') return this;

    const stopTime = time ?? this.audioContext.currentTime;
    this._state = 'stopped';
    this._tickOffset = 0;
    this.lastScheduledTick = -1;
    this.stopScheduler();
    this.emit('stop', stopTime);

    return this;
  }

  /** Pause the transport (preserves position) */
  pause(time?: number): this {
    if (this._state !== 'started') return this;

    const pauseTime = time ?? this.audioContext.currentTime;
    this._tickOffset = this.ticks;  // Save current position
    this._pauseTime = pauseTime;
    this._state = 'paused';
    this.stopScheduler();
    this.emit('pause', pauseTime);

    return this;
  }

  /** Toggle between started and stopped */
  toggle(): this {
    if (this._state === 'started') {
      this.stop();
    } else {
      this.start();
    }
    return this;
  }

  // ─────────────────────────────────────────────────────────────
  // Scheduling
  // ─────────────────────────────────────────────────────────────

  /**
   * Schedule a callback at a specific time
   * @param callback - Called with precise audioContext time
   * @param time - When to invoke (ticks, notation, or bars:beats:sixteenths)
   * @returns Event ID for cancellation
   */
  schedule(callback: TransportCallback, time: Time): number {
    const id = this.nextEventId++;
    const event: ScheduledEvent = {
      id,
      callback,
      time: this.toTicks(time),
      disposed: false
    };
    this.events.set(id, event);
    return id;
  }

  /**
   * Schedule a callback once (removed after invocation)
   * @param callback - Called with precise audioContext time
   * @param time - When to invoke
   * @returns Event ID for cancellation
   */
  scheduleOnce(callback: TransportCallback, time: Time): number {
    const id = this.nextEventId++;
    const wrappedCallback: TransportCallback = (t) => {
      callback(t);
      this.clear(id);
    };
    const event: ScheduledEvent = {
      id,
      callback: wrappedCallback,
      time: this.toTicks(time),
      disposed: false
    };
    this.events.set(id, event);
    return id;
  }

  /**
   * Schedule a repeating callback
   * @param callback - Called with precise audioContext time
   * @param interval - Time between invocations
   * @param startTime - When to start (default: 0)
   * @param duration - How long to repeat (default: Infinity)
   * @returns Event ID for cancellation
   */
  scheduleRepeat(
    callback: TransportCallback,
    interval: Time,
    startTime: Time = 0,
    duration: Time = Infinity
  ): number {
    const id = this.nextEventId++;
    const intervalTicks = this.toTicks(interval);
    const startTicks = this.toTicks(startTime);
    const durationTicks = typeof duration === 'number' && duration === Infinity
      ? Infinity
      : this.toTicks(duration);

    const event: ScheduledEvent = {
      id,
      callback,
      time: startTicks,
      repeat: intervalTicks,
      duration: durationTicks,
      disposed: false
    };
    this.events.set(id, event);
    return id;
  }

  /**
   * Clear a scheduled event
   * @param eventId - The ID returned from schedule/scheduleRepeat
   */
  clear(eventId: number): this {
    const event = this.events.get(eventId);
    if (event) {
      event.disposed = true;
      this.events.delete(eventId);
    }
    return this;
  }

  /**
   * Cancel all events after a given time
   * @param after - Cancel events scheduled after this time (default: 0)
   */
  cancel(after: Time = 0): this {
    const afterTicks = this.toTicks(after);
    for (const [id, event] of this.events) {
      if (event.time >= afterTicks) {
        this.clear(id);
      }
    }
    return this;
  }

  // ─────────────────────────────────────────────────────────────
  // Event Emitter
  // ─────────────────────────────────────────────────────────────

  /** Subscribe to transport events */
  on(event: TransportEventName, callback: TransportCallback): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
    return this;
  }

  /** Unsubscribe from transport events */
  off(event: TransportEventName, callback: TransportCallback): this {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
    return this;
  }

  /** Emit a transport event */
  private emit(event: TransportEventName, time: number): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      for (const callback of listeners) {
        callback(time);
      }
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Internal Scheduler
  // ─────────────────────────────────────────────────────────────

  private startScheduler(): void {
    if (this.schedulerTimer !== null) return;

    this.schedulerTimer = setInterval(() => {
      this.processSchedule();
    }, this.updateInterval);
  }

  private stopScheduler(): void {
    if (this.schedulerTimer !== null) {
      clearInterval(this.schedulerTimer);
      this.schedulerTimer = null;
    }
  }

  /**
   * Main scheduler loop - called every updateInterval ms
   * Looks ahead and invokes callbacks with precise time
   */
  private processSchedule(): void {
    if (this._state !== 'started') return;

    const now = this.audioContext.currentTime;
    const currentTick = this.ticks;
    const lookaheadTicks = this.secondsToTicks(this.lookahead);
    const endTick = currentTick + lookaheadTicks;

    // Process each event
    for (const event of this.events.values()) {
      if (event.disposed) continue;

      if (event.repeat !== undefined) {
        // Repeating event
        this.processRepeatEvent(event, currentTick, endTick, now);
      } else {
        // One-shot event
        if (event.time > this.lastScheduledTick && event.time <= endTick) {
          const eventTime = this.ticksToAudioTime(event.time);
          event.callback(eventTime);
        }
      }
    }

    this.lastScheduledTick = endTick;
  }

  private processRepeatEvent(
    event: ScheduledEvent,
    currentTick: number,
    endTick: number,
    now: number
  ): void {
    const { time: startTick, repeat, duration, callback } = event;
    if (repeat === undefined) return;

    // Calculate which iterations fall within the lookahead window
    const endAt = duration === Infinity ? Infinity : startTick + duration!;

    // Find the first iteration that hasn't been scheduled yet
    let nextTick = startTick;
    if (this.lastScheduledTick >= startTick) {
      // Calculate how many iterations have already happened
      const ticksSinceStart = this.lastScheduledTick - startTick;
      const iterationsPassed = Math.floor(ticksSinceStart / repeat) + 1;
      nextTick = startTick + iterationsPassed * repeat;
    }

    // Schedule all iterations within the lookahead window
    while (nextTick <= endTick && nextTick < endAt) {
      if (nextTick > this.lastScheduledTick) {
        const eventTime = this.ticksToAudioTime(nextTick);
        callback(eventTime);
      }
      nextTick += repeat;
    }
  }

  /** Convert a tick position to audioContext time */
  private ticksToAudioTime(tick: number): number {
    const ticksFromStart = tick - this._tickOffset;
    const secondsFromStart = this.ticksToSeconds(ticksFromStart);
    return this._startTime + secondsFromStart;
  }

  // ─────────────────────────────────────────────────────────────
  // Cleanup
  // ─────────────────────────────────────────────────────────────

  /** Dispose the transport */
  dispose(): void {
    this.stop();
    this.events.clear();
    this.listeners.clear();
  }
}
